import {
    useState, useCallback, useContext, useEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import {
    Row, Col, Button, FormGroup, Label, Alert, Badge, Card, CardHeader, CardBody,
} from 'reactstrap';
import moment from 'moment';
import { faFileExcel, faLocationDot, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import SlideForm from '../../common/forms/SlideForm';
import { SaveButton } from '../../common/forms/SaveButton';
import { OrganizationContactSelect } from '../../organization/OrganizationContactSelect';
import { FlexEndRow, toasty, Input } from '../../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../../Common';
import AddressFormNew from '../../address/AddressFormNew';
import { Customer } from './Customer';

const buildNotes = (r) => [
    r.time ? `Start time: ${r.time} ` : null,
    r.additionalEquipment ? `Additional Equipment: ${r.additionalEquipment} ` : null,
    r.notes ? r.notes : null,
].filter(Boolean).join('\n');

export const JobRequestXlsxImportSlideout = ({ show, onClose, onImported }) => {
    const { tenant } = useContext(CommonContext);
    const addressFormRef = useRef(null);
    const formRef = useRef(null);
    const [isValidated, setIsValidated] = useState(false);
    const [step, setStep] = useState('upload');
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [editingLocationRowIndex, setEditingLocationRowIndex] = useState(null);

    const hasUngeocodedRows = rows.some((r) => !r.latitude);

    useEffect(() => {
        if (show) {
            setIsValidated(false);
        }
    }, [show]);

    const handleFileChange = useCallback((e) => {
        setFile(e.target.files[0] ?? null);
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        setStep('upload');
        setFile(null);
        setRows([]);
        setError(null);
        setEditingLocationRowIndex(null);
        onClose();
    }, [onClose]);

    const handleUpload = useCallback(async (values) => {
        setError(null);
        try {
            const response = await httpClient.postFile(ApiRoutes.jobRequest.parseXlsx(), {
                file,
                customerCompanyId: values.customerCompanyId,
            });
            if (!response.ok) throw new Error('Invalid file format. Please upload an Excel file (.xlsx) and try again.');
            const parsed = await response.json();

            if (!parsed.length) {
                setError('No schedule rows found in this file.');
                return;
            }
            setRows(parsed.map((r) => ({
                ...r,
                address1: r.geocodedAddress1 ?? r.streetLocation,
                city: r.geocodedCity ?? r.area,
                state: r.geocodedState ?? '',
                zip: r.geocodedZip ?? '',
                jobDescription: `${r.sheetName} - ${r.area}`,
                notes: buildNotes(r),
            })));

            const nextValues = { customerCompanyId: values.customerCompanyId };
            parsed.forEach((r) => {
                nextValues[`foreman_${r.rowIndex}`] = r.foremanMatched ? r.matchedLocationContactId : null;
            });
            formRef.current.setValues(nextValues);

            setIsValidated(false);
            setStep('results');
        } catch (e) {
            setError(e.message ?? 'Invalid file format. Please upload an Excel file (.xlsx) and try again.');
        }
    }, [file]);

    const handleBulkSubmit = useCallback(async (values) => {
        setError(null);
        try {
            await httpClient.post(ApiRoutes.jobRequest.bulkCreate(), {
                customerCompanyId: values.customerCompanyId,
                rows: rows.map(({ rowIndex, ...row }) => ({
                    ...row,
                    locationContactId: values[`foreman_${rowIndex}`],
                })),
            });
            toasty.success(`${rows.length} Job Request(s) created successfully.`);
            onImported();
            handleClose();
        } catch (e) {
            setError(e.message ?? 'Failed to create job requests.');
        }
    }, [rows, onImported, handleClose]);

    const handleSubmit = useCallback(async (values) => {
        if (step === 'upload') {
            await handleUpload(values);
        } else {
            await handleBulkSubmit(values);
        }
    }, [step, handleUpload, handleBulkSubmit]);

    const updateRow = useCallback((rowIndex, patch) => {
        setRows((prev) => prev.map((r) => (r.rowIndex === rowIndex ? { ...r, ...patch } : r)));
    }, []);

    const openAddressEdit = useCallback((row) => {
        setEditingLocationRowIndex(row.rowIndex);
        addressFormRef.current.open(
            {
                ...row,
                zip: row.zip ?? '',
                countryId: row.countryId ?? tenant?.countryId,
                countryCode: row.countryCode ?? tenant?.countryCode,
            },
            'Edit Location',
        );
    }, [tenant?.countryId, tenant?.countryCode]);

    const onAddressSaved = useCallback((address) => {
        if (editingLocationRowIndex === null) return;
        updateRow(editingLocationRowIndex, address);
        setEditingLocationRowIndex(null);
    }, [editingLocationRowIndex, updateRow]);

    return createPortal(
        <>
            <SlideForm
                size="col-xl-12 col-md-12 col-xs-12"
                id="jobRequestXlsxImportForm"
                name="jobRequestXlsxImportForm"
                show={show}
                onClose={handleClose}
                fullScreen
                hideToolbar
                formRef={formRef}
                formTitle="Import Job Requests from XLSX"
                formIcon={faFileExcel}
                onSubmit={handleSubmit}
                setIsValidated={(v) => setIsValidated(v)}
                isValidated={isValidated}
                initialValues={{ customerCompanyId: null }}
            >
                {({ values, saving }) => {
                    const hasMissingForeman = rows.some(
                        (row) => !values[`foreman_${row.rowIndex}`],
                    );

                    return (
                        <>
                            {error && (
                                <Alert
                                    color="danger"
                                    className="mb-3"
                                >
                                    {error}
                                </Alert>
                            )}

                            {step === 'upload' && (
                                <>
                                    <Customer />

                                    <FormGroup>
                                        <input
                                            id="jobRequestXlsxFile"
                                            type="file"
                                            className="form-control-file mb-1"
                                            accept=".xlsx"
                                            required
                                            onChange={handleFileChange}
                                        />
                                        <small className="invalid-feedback text-danger">
                                            File is required
                                        </small>
                                    </FormGroup>

                                    <SaveButton
                                        label="Upload"
                                        savingLabel="Uploading"
                                        icon={faFileExcel}
                                    />
                                </>
                            )}

                            {step === 'results' && (
                                <>
                                    <Alert
                                        color="info"
                                        className="mb-3"
                                    >
                                        {rows.length}
                                        {' '}
                                        row(s) parsed. Review and correct before submitting.
                                    </Alert>
                                    {rows.map((row) => (
                                        <Card
                                            key={row.rowIndex}
                                            className="mb-3"
                                        >
                                            <CardHeader
                                                className="bg-light py-2 px-3"
                                                style={{ fontSize: '0.85rem' }}
                                            >
                                                <strong>{row.sheetName}</strong>
                                                {' · '}
                                                {moment.parseZone(row.requestedStartDate).format('ddd MM/DD/YYYY HH:mm')}
                                                {' · '}
                                                {row.foremanRaw}
                                                {' · '}
                                                {row.streetLocation}
                                                {row.area ? `, ${row.area}` : ''}
                                                {row.notes ? ` · ${row.notes}` : ''}
                                            </CardHeader>
                                            <CardBody className="py-2 px-3">
                                                <Row>
                                                    <Col md={3}>
                                                        <FormGroup>
                                                            <Input
                                                                label="Requested Date"
                                                                required
                                                                type="date"
                                                                value={moment.parseZone(row.requestedStartDate).format('YYYY-MM-DD')}
                                                                onChange={(e) => e.target.value && updateRow(row.rowIndex, {
                                                                    requestedStartDate: e.target.value + row.requestedStartDate.slice(10),
                                                                })}
                                                            />
                                                            {!row.foremanMatched && !values[`foreman_${row.rowIndex}`] && (
                                                                <Badge
                                                                    color="warning"
                                                                    className="ml-2 mb-1"
                                                                >
                                                                    No match found
                                                                </Badge>
                                                            )}
                                                            <OrganizationContactSelect
                                                                id={`foreman_${row.rowIndex}`}
                                                                label="Foreman"
                                                                name={`foreman_${row.rowIndex}`}
                                                                companyId={values.customerCompanyId}
                                                                required
                                                                showAddButton
                                                                error="You must select a foreman"
                                                                placeholder="Select foreman..."
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Input
                                                            label="Job Description"
                                                            type="textarea"
                                                            rows={3}
                                                            value={row.jobDescription}
                                                            onChange={(e) => updateRow(row.rowIndex, { jobDescription: e.target.value })}
                                                        />
                                                        <Input
                                                            label="WO Number"
                                                            value={row.workOrderNumber}
                                                            onChange={(e) => updateRow(row.rowIndex, { workOrderNumber: e.target.value })}
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Input
                                                            type="textarea"
                                                            rows={3}
                                                            label="Notes"
                                                            value={row.notes}
                                                            onChange={(e) => updateRow(row.rowIndex, { notes: e.target.value })}
                                                        />

                                                        <Input
                                                            label="PO Number"
                                                            value={row.purchaseOrderNumber}
                                                            onChange={(e) => updateRow(row.rowIndex, { purchaseOrderNumber: e.target.value })}
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Label className="small text-muted mb-1">Location</Label>
                                                        {row.latitude ? (
                                                            <div className="small">
                                                                <FontAwesomeIcon
                                                                    icon={faLocationDot}
                                                                    className="text-success mr-1"
                                                                />
                                                                <div>{row.address1}</div>
                                                                <div>
                                                                    {row.city}
                                                                    {row.state ? `, ${row.state}` : ''}
                                                                </div>
                                                                <div className="text-muted">
                                                                    {row.latitude?.toFixed(5)}
                                                                    {', '}
                                                                    {row.longitude?.toFixed(5)}
                                                                </div>
                                                                {row.timeZone && <div className="text-muted">{row.timeZone}</div>}
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <Badge
                                                                    color="warning"
                                                                    className="mb-1"
                                                                >
                                                                    Not geocoded
                                                                </Badge>
                                                                <div className="small">
                                                                    {row.address1}
                                                                    {row.city ? `, ${row.city}` : ''}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            className="p-0 mt-1"
                                                            type="button"
                                                            onClick={() => openAddressEdit(row)}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faPencil}
                                                                className="mr-1"
                                                            />
                                                            Edit
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    ))}
                                    {hasUngeocodedRows && (
                                        <Alert
                                            color="warning"
                                            className="mt-3 mb-0"
                                        >
                                            Some locations are not geocoded. Use Edit on each flagged location before submitting.
                                        </Alert>
                                    )}
                                    <FlexEndRow className="mt-3">
                                        <Button
                                            color="secondary"
                                            type="button"
                                            className="mr-2"
                                            onClick={() => setStep('upload')}
                                            disabled={saving}
                                        >
                                            Back
                                        </Button>
                                        <SaveButton
                                            label={`Submit ${rows.length} Job Request(s)`}
                                            savingLabel="Submitting"
                                            disabled={hasUngeocodedRows || hasMissingForeman}
                                        />
                                    </FlexEndRow>
                                </>
                            )}
                        </>
                    );
                }}
            </SlideForm>

            <AddressFormNew
                ref={addressFormRef}
                countryId={tenant?.countryId}
                countryCode={tenant?.countryCode}
                onSaveCallback={onAddressSaved}
            />
        </>,
        document.body,
    );
};

export default JobRequestXlsxImportSlideout;
